/**
 * LoadError component - displays authentication/loading error.
 *
 * Variants:
 * - `network` — the device cannot reach Ahoy. Copy branches on
 *   `navigator.onLine`: fully offline vs. online-but-Ahoy-blocked.
 * - `server` — Ahoy responded, but with an error.
 * - `auth` — the auth bootstrap response was invalid.
 *
 * Network and server variants retry automatically (countdown + reload) and
 * reload immediately when the browser regains connectivity. Auto-retry is
 * paused while the diagnostics report modal is open so an in-progress report
 * is never destroyed by a reload.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { DiagnosticsReportModal } from '@/lib/diagnostics/DiagnosticsReportModal';
import { ReportProblemButton } from '@/lib/diagnostics/ReportProblemButton';
import { AhoyLogo } from '@/lib/foundations/logo/ahoy-logo';

const handleRetry = () => window.location.reload();

const AUTO_RETRY_SECONDS = 15;

const getIsOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine;

interface ErrorShellProps {
  heading: string;
  body: ReactNode;
  actions: ReactNode;
  footer?: ReactNode;
}

function ErrorShell({ heading, body, actions, footer }: ErrorShellProps) {
  return (
    <div className='flex items-center justify-center min-h-full bg-[var(--color-bg-primary)]'>
      <div
        className='flex flex-col items-center text-center max-w-md px-4'
        role='alert'
      >
        <AhoyLogo size='md' className='mb-6' />
        <h1 className='text-2xl font-bold text-[var(--color-text-primary)] mb-2'>
          {heading}
        </h1>
        {body}
        {actions}
        {footer}
      </div>
    </div>
  );
}

interface LoadErrorProps {
  errorType?: 'server' | 'auth' | 'network';
  loginUrl?: string;
}

export default function LoadError({
  errorType = 'auth',
  loginUrl = '/account/login',
}: LoadErrorProps) {
  const [reportOpen, setReportOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(getIsOnline);
  const [secondsToRetry, setSecondsToRetry] = useState(AUTO_RETRY_SECONDS);
  const openReport = useCallback(() => setReportOpen(true), []);
  const closeReport = useCallback(() => setReportOpen(false), []);

  const autoRetryEnabled = errorType !== 'auth' && isOnline && !reportOpen;

  // Track connectivity; reload as soon as the connection comes back (unless
  // the user is mid-report or the error is not connectivity-related).
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (errorType !== 'auth' && !reportOpen) {
        handleRetry();
      }
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [errorType, reportOpen]);

  // Countdown auto-retry for transient failures.
  useEffect(() => {
    if (!autoRetryEnabled) {
      return undefined;
    }
    setSecondsToRetry(AUTO_RETRY_SECONDS);
    const interval = setInterval(() => {
      setSecondsToRetry(seconds => {
        if (seconds <= 1) {
          handleRetry();
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [autoRetryEnabled]);

  const retryActions = (
    <div className='flex items-center justify-center gap-2'>
      <Button onClick={handleRetry} color='primary' size='md'>
        Try again
      </Button>
      <ReportProblemButton onClick={openReport} />
    </div>
  );

  let retryFooter = null;
  if (errorType !== 'auth' && !reportOpen) {
    retryFooter = (
      <p
        className='text-sm text-[var(--color-text-tertiary)] mt-4'
        role='status'
      >
        {isOnline
          ? `Retrying automatically in ${secondsToRetry}s…`
          : "We'll retry automatically when your connection is back."}
      </p>
    );
  }

  let content;

  if (errorType === 'network' && !isOnline) {
    content = (
      <ErrorShell
        heading="You're offline"
        body={
          <p className='text-[var(--color-text-secondary)] mb-4'>
            This device isn&apos;t connected to the internet. Reconnect, then
            try again.
          </p>
        }
        actions={retryActions}
        footer={retryFooter}
      />
    );
  } else if (errorType === 'network') {
    content = (
      <ErrorShell
        heading='Check your connection'
        body={
          <p className='text-[var(--color-text-secondary)] mb-4'>
            Your internet looks fine, but we can&apos;t reach Ahoy from this
            device. A firewall or network filter may be blocking it — report the
            problem and we&apos;ll help you sort it out.
          </p>
        }
        actions={retryActions}
        footer={retryFooter}
      />
    );
  } else if (errorType === 'server') {
    content = (
      <ErrorShell
        heading="We're having trouble connecting"
        body={
          <p className='text-[var(--color-text-secondary)] mb-4'>
            Our servers are temporarily unavailable. This usually resolves
            quickly.
          </p>
        }
        actions={retryActions}
        footer={retryFooter}
      />
    );
  } else {
    content = (
      <ErrorShell
        heading='Authentication error'
        body={
          <>
            <p className='text-[var(--color-text-secondary)] mb-4'>
              There was a problem loading your authentication status.
            </p>
            <p className='text-sm text-[var(--color-text-secondary)] mb-4'>
              Please try refreshing the page or{' '}
              <a
                href={loginUrl}
                className='text-fg-brand-primary hover:text-[var(--color-brand-500)]'
              >
                sign in again
              </a>
              .
            </p>
          </>
        }
        actions={<ReportProblemButton onClick={openReport} />}
      />
    );
  }

  return (
    <>
      {content}
      <DiagnosticsReportModal
        isOpen={reportOpen}
        onClose={closeReport}
        trigger={`load_error:${errorType}`}
      />
    </>
  );
}
